import React, { createContext, useContext, useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home, History, Award, User, Settings, LogOut, Heart, Menu, X,
  Gift, TrendingUp, Calendar, Bell, ChevronDown, Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, 
  DropdownMenuSeparator, DropdownMenuTrigger
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Context de autenticação do doador
const DoadorAuthContext = createContext();

export const useDoadorAuth = () => useContext(DoadorAuthContext);

// Provider de autenticação
export const DoadorAuthProvider = ({ children }) => {
  const [doador, setDoador] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('doador_token');
    if (token) {
      fetchDoador(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchDoador = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/doador/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDoador(data);
      } else {
        localStorage.removeItem('doador_token');
      }
    } catch (e) {
      console.error('Erro ao obter doador:', e);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const res = await fetch(`${API_URL}/api/doador/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        localStorage.setItem('doador_token', data.access_token);
        setDoador({ doador_id: data.doador?.id, email, nome: data.user?.nome, doador: data.doador });
        
        if (data.user?.password_temporaria) {
          toast.info('Por favor altere a sua password temporária');
          navigate('/doador/alterar-password');
        } else {
          navigate('/doador/dashboard');
        }
        return { success: true };
      }
      return { success: false, error: data.detail || 'Erro no login' };
    } catch (e) {
      return { success: false, error: 'Erro de conexão' };
    }
  };

  const logout = () => {
    localStorage.removeItem('doador_token');
    setDoador(null);
    navigate('/doador/login');
  };

  return (
    <DoadorAuthContext.Provider value={{ doador, loading, login, logout, fetchDoador }}>
      {children}
    </DoadorAuthContext.Provider>
  );
};

// API helper
export const doadorApiCall = async (endpoint, options = {}) => {
  const token = localStorage.getItem('doador_token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  
  const response = await fetch(`${API_URL}/api${endpoint}`, { ...options, headers });
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem('doador_token');
      window.location.href = '/doador/login';
    }
    const error = await response.json().catch(() => ({ detail: 'Erro' }));
    throw new Error(error.detail || 'Erro na requisição');
  }
  return response.json();
};

// Menu items
const MENU_ITEMS = [
  { path: '/doador/dashboard', label: 'Dashboard', icon: Home },
  { path: '/doador/doacoes', label: 'Minhas Doações', icon: History },
  { path: '/doador/certificados', label: 'Certificados', icon: Award },
  { path: '/doador/campanhas', label: 'Campanhas', icon: Target },
  { path: '/doador/pontos', label: 'Pontos & Badges', icon: Gift },
  { path: '/doador/perfil', label: 'Meu Perfil', icon: User },
];

// Layout principal
export default function DoadorPortalLayout() {
  const { doador, loading, logout } = useDoadorAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !doador) {
      navigate('/doador/login');
    }
  }, [loading, doador, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 to-amber-50">
        <div className="w-12 h-12 border-3 border-rose-200 border-t-rose-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!doador) return null;

  const initials = doador.nome?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || 'D';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-rose-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-lg border-b border-rose-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-4">
              <button 
                className="lg:hidden p-2 text-slate-600 hover:text-rose-600"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <Link to="/doador/dashboard" className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-amber-500 rounded-xl flex items-center justify-center">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-lg font-bold text-slate-900">Portal do Doador</h1>
                  <p className="text-xs text-slate-500">ENTRAJUDA</p>
                </div>
              </Link>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              <Badge className="hidden sm:flex bg-rose-100 text-rose-700 hover:bg-rose-200">
                <Gift className="w-3 h-3 mr-1" />
                {doador.doador?.pontos || 0} pontos
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <Avatar className="w-8 h-8">
                      <AvatarFallback className="bg-gradient-to-br from-rose-500 to-amber-500 text-white text-xs">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden sm:inline text-sm font-medium">{doador.nome}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{doador.nome}</p>
                    <p className="text-xs text-slate-500">{doador.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/doador/perfil')}>
                    <User className="w-4 h-4 mr-2" />
                    Meu Perfil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/doador/alterar-password')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Alterar Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <aside className={`
            fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white rounded-2xl shadow-lg p-4
            transform transition-transform duration-300 ease-in-out
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            top-16 lg:top-0 h-[calc(100vh-4rem)] lg:h-auto
          `}>
            <nav className="space-y-1">
              {MENU_ITEMS.map(item => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isActive 
                        ? 'bg-gradient-to-r from-rose-500 to-amber-500 text-white shadow-md' 
                        : 'text-slate-600 hover:bg-rose-50 hover:text-rose-600'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Quick Stats */}
            <div className="mt-6 p-4 bg-gradient-to-br from-rose-50 to-amber-50 rounded-xl">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Seu Impacto</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Total doações</span>
                  <span className="font-bold text-rose-600">{doador.doador?.total_doacoes || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Badges</span>
                  <span className="font-bold text-amber-600">{doador.doador?.badges?.length || 0}</span>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-4">
              <Button 
                className="w-full bg-gradient-to-r from-rose-500 to-amber-500 hover:from-rose-600 hover:to-amber-600"
                onClick={() => navigate('/doador/campanhas')}
              >
                <Heart className="w-4 h-4 mr-2" />
                Fazer Doação
              </Button>
            </div>
          </aside>

          {/* Overlay para mobile */}
          {sidebarOpen && (
            <div 
              className="fixed inset-0 bg-black/50 z-30 lg:hidden"
              onClick={() => setSidebarOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
